import { useFocus } from '@/app/hooks/useFocus'
import Button from '@/components/Button/Button'
import Icon from '@/components/Icon/Icon'
import TextField from '@/components/TextField/TextField'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { match, P } from 'ts-pattern'

type AA = 'AM' | 'PM'
export default function DesktopTimePicker({
  defaultValue,
  onChange,
  suggestionHeight = 44,
}: {
  defaultValue?: Dayjs
  onChange: (date: Dayjs) => Dayjs
  suggestionHeight?: number
}) {
  const [aa, setAA] = useState(
    (defaultValue?.format('A') as AA) ?? ('AM' as AA),
  )
  const [time, setTime] = useState(defaultValue?.format('h:mm') ?? '')
  const [suggestions, setSuggestions] = useState(allDaySuggestions())
  const [is24h, setIs24h] = useState(false)
  const [targetTime, setTargetTime] = useState<Dayjs | null>(null)
  // regex for time with optional minutes i.e. 8:00, 8
  const validTime = /^([0-1]?[0-9]|2[0-3]):?([0-5][0-9]|[0-9])?$/
  const [inputRef, setInputFocus] = useFocus()
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const updateSuggestions = (hour: number, aa?: AA) => {
    // Generate all 24 hours in 30-minute increments (48 total)
    const allSuggestions = allDaySuggestions()
    setSuggestions(allSuggestions)

    // Set the target time to scroll to
    const target = hourToDayjs(hour, aa).minute(0)
    setTargetTime(target)
  }
  useEffect(() => {
    setIs24h(false)
    if (time.length === 0 || !validTime.test(time)) {
      setSuggestions(allDaySuggestions())
      setTargetTime(null)
      return
    }
    const times = time.split(':')
    const hour = parseInt(times[0])

    // Auto-sync AM/PM toggle based on input
    match(hour)
      .with(
        P.when((h) => h > 12),
        () => {
          // 24-hour format (e.g., 14:00)
          setIs24h(true)
          setAA('PM')
        },
      )
      .otherwise(() => {
        // For hours 1-12, keep current toggle state (user's choice)
      })

    updateSuggestions(hour, aa)
  }, [time, aa])

  // Scroll to the target time when suggestions or target changes
  useEffect(() => {
    if (targetTime && suggestionsRef.current) {
      // Find the index of the suggestion closest to the target time
      const targetIndex = suggestions.findIndex(
        (s) =>
          s.hour() === targetTime.hour() && s.minute() === targetTime.minute(),
      )

      if (targetIndex !== -1) {
        const container = suggestionsRef.current
        const itemHeight = suggestionHeight
        const containerHeight = container.clientHeight
        const scrollPosition = Math.max(
          0,
          targetIndex * itemHeight - containerHeight / 2 + itemHeight / 2,
        )

        container.scrollTo?.({
          top: scrollPosition,
          behavior: 'instant',
        })
      }
    }
  }, [targetTime, suggestions])
  useEffect(() => {
    // focus input on mount
    setInputFocus()
  }, [])
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-4 items-end px-2">
        <TextField
          value={time}
          label="Time"
          placeholder="8:00"
          error={!validTime.test(time) && time.length > 0}
          className="w-20"
          autoFocus
          inputRef={inputRef}
          onChange={(e) => setTime(e.currentTarget.value)}
        />
        <div className="flex">
          <Button
            id="desktop-time-picker-am-button"
            className="rounded-r-none p-4 px-6 w-4 h-6 border-r-0 focus:ring-2 focus:ring-themed-blue-400 focus:ring-offset-2"
            variant={aa === 'AM' ? 'accent' : 'accent-subtle'}
            disabled={is24h}
            onClick={() => setAA('AM')}
          >
            AM
          </Button>
          <Button
            id="desktop-time-picker-pm-button"
            className="rounded-l-none p-4 px-6 w-4 h-6 focus:ring-2 focus:ring-themed-blue-400 focus:ring-offset-2"
            variant={aa === 'PM' ? 'accent' : 'accent-subtle'}
            onClick={() => setAA('PM')}
          >
            PM
          </Button>
        </div>
      </div>
      <div
        ref={suggestionsRef}
        className="flex flex-col max-h-[200px] overflow-y-auto"
      >
        {suggestions.map((s, i) => (
          <div
            key={i}
            className="flex flex-row justify-between items-center hover:bg-gray-100 rounded-lg p-2 hover:cursor-pointer group"
            onClick={() => onChange(s)}
          >
            <div>{s.format('h:mm A')}</div>
            <div className="hidden group-hover:block">
              <Icon name="Check" size={14} className="translate-y-[2px]" />
            </div>
          </div>
        ))}
      </div>
      <Button
        id="desktop-datepicker-ok-button"
        disabled={!validTime.test(time)}
        className="mt-2 p-6 w-full h-6"
        onClick={() => onChange(handleSubmit(time, aa))}
      >
        Apply
      </Button>
    </div>
  )
}

function hourToDayjs(hour: number, aa?: AA) {
  // Convert 12-hour time to 24-hour format
  // Default hour 12 to PM (noon) when no AM/PM specified
  const amPm = aa ?? (hour === 12 ? 'PM' : 'AM')

  const hour24 = match({ hour, amPm })
    .with({ hour: 12, amPm: 'AM' }, () => 0) // 12 AM = 0 (midnight)
    .with({ hour: 12, amPm: 'PM' }, () => 12) // 12 PM = 12 (noon)
    .with({ hour: P.when((h) => h > 12) }, ({ hour }) => hour) // 24-hour format input (e.g., 14:00)
    .with({ amPm: 'PM' }, ({ hour }) => hour + 12) // 1-11 PM: add 12
    .with({ amPm: 'AM' }, ({ hour }) => hour) // 1-11 AM: keep as-is
    .exhaustive()

  return dayjs().hour(hour24).minute(0).second(0)
}

function handleSubmit(time: string, aa: AA) {
  const times = time.split(':')
  const hour = parseInt(times[0])
  const minute = times.length > 1 && times[1] ? parseInt(times[1]) : 0
  return hourToDayjs(hour, aa).minute(minute)
}

function allDaySuggestions() {
  // Generate all 24 hours in 30-minute increments (48 total)
  return [...Array(48).keys()].map((i) => {
    const totalMinutes = i * 30
    const hour = Math.floor(totalMinutes / 60)
    const minute = totalMinutes % 60
    return dayjs().hour(hour).minute(minute).second(0)
  })
}
